import { getType } from "typesafe-actions";
import * as types from "./types";
import * as actions from "./actions";
import _isEmpty from "lodash-es/isEmpty";
import _findIndex from "lodash-es/findIndex";
import { PayeeType } from "../Payee/reducers";
import { CollectionRates } from "../CollectedAccount/reducers";

export type RefundCommonAttrs = {
  actionType: string;
  chargeDate: string;
  createdAt: string;
  createdBy: {
    email: string;
    firstName: string;
    lastName: string;
    userId: number;
  };
  email: string;
  paymentId: number;
  paymentAmount: number;
  paymentFees: number;
  paymentTotal: number;
  payoutDate: string;
  requestId: number;
  receiptNumber: number;
  scheduledChargeDate: string;
  isInternationalPayment: boolean;
};

export type RefundType = RefundCommonAttrs & {
  metaData: {
    newPaymentFees: number;
    newPaymentTotal: number;
    newPayoutDate: string;
    newRate: number;
    otherRefusalReason: string;
    paymentRefNo: string;
    refundedFee?: number;
    refundedAmount?: number;
    refundedTotal?: number;
    refusalReason: string;
    refusalReasonCode: string;
    bankId: number;
    comments: string;
    accountNumber: string;
    intercomLink?: string;
  };
};

export type EditPaymentPayeeRequest = RefundCommonAttrs & {
  metaData: [
    {
      payeeId: number;
      bankId: number;
      comments: string;
      accountNumber: string;
      bankRawName: string;
      recipientName: string;
    }
  ];
  oldMetaData: [
    {
      payeeId: number;
      bankId: number;
      comments: string;
      accountNumber: string;
      bankRawName: string;
      recipientName: string;
    }
  ];
};

export type ScheduleType = {
  id: number;
  isCancelled?: boolean;
  purposeId: number;
  scheduleStartDate: string;
  scheduleEndDate: string;
  scheduleFrequency: string;
  scheduleStatus?: string;
  cards?: [
    {
      id: number;
      statementDescriptor: string;
      amount?: number;
      token?: string;
      last4?: string;
      cardBrand?: number;
    }
  ];
  paymentAmount: number;
  paymentStatusId?: number;
  paymentFees: number;
  paymentInstantPayFees?: number;
  paymentFlashPayFees?: number;
  paymentGSTFees?: number;
  paymentBankPayoutFees?: number;
  paymentMinimumTransactionFees?: number;
  paymentTotal: number;
  mastercardMoveTransactionFees?: number;
  mastercardMoveTotalAmount?: number;
  mastercardMovePayoutCountryId?: number;
  scheduleId: number;
  payees: PayeeType[];
  upcomingPayment?: {
    payments: UpcomingPaymentType[];
  };
  paymentSetting?: PaymentType;
  lastPayout?: string;
  hasSpecialRate?: boolean;
  wallexReference?: string;
  payoutReference?: string;
  payoutPartyId?: number;
  scheduledChargeDate?: string;
  company?: string;
  countryId?: number; // payer account's default_country_id
  creatorEmail?: string;
  createdBy?: number;
  payoutDate?: string;
  upComingPayoutDate?: string;
  lastPayoutDate?: string;
  lastScheduledChargeDate?: string;
  noOfScheduledPayments?: number;
  accountId?: number;
};

export type ScheduleRequestType = {
  id: number;
  scheduleId: number;
  actionType: string;
  currencyId: number;
  paymentAmount: number;
  paymentFees: number;
  paymentTotal: number;
  payoutDate: string;
  scheduledChargeDate: string;
  scheduleStartDate: string;
  scheduleEndDate: string;
  createdAt: string;
  createdBy: {
    email: string;
    firstName: string;
    lastName: string;
    userId: number;
  };
  creatorEmail: string;
  metaData: {
    paymentStatusId?: number;
    supportingDocuments?: Array<{
      key: string;
      name: string;
      size: number;
      url: string;
    }>;
    newPayoutDate?: string;
    payees?: Array<{
      comments: string;
      id: number;
      uid: string;
      bankId: number;
      accountNumber: string;
      bank_raw_name?: string;
    }>;
  };
  oldMetaData: {
    currentStatusId?: number;
    supportingDocuments?: Array<{
      key: string;
      name: string;
      size: number;
      url: string;
    }>;
    currentPayoutDate?: string;
    payees?: Array<{
      comments: string;
      id: number;
      uid: string;
      bankId: number;
      accountNumber: string;
      bank_raw_name?: string;
    }>;
  };
} & ProcessedByType;

export type UpcomingPaymentType = {
  id: number;
  chargeDate: string;
  payoutDate: string;
  paymentFee: number;
  paymentAmount: number;
  paymentTotal: number;
  cards: [
    {
      id: number;
      statementDescriptor: string;
      amount?: number;
      token?: string;
      last4?: string;
      cardBrand?: number;
    }
  ];
  defaultComments: string;
  editable: boolean;
};

export type RefundHistoryItem = {
  type: string;
  amount: number;
  requestedAt?: string;
  approvedAt?: string;
  confirmedAt?: string;
  status: string;
};

export type PaymentType = ScheduleType & {
  accountId?: number;
  id: number;
  cardCharge?: [
    {
      amount: number;
      chargeDate: string;
      id: string;
      receiptNumber: string;
      status: string;
      refuseReason?: string[];
      refundedAmount: number;
      refundedReason?: string[];
      refundedReasonCode?: string;
      refundedFee: number;
      refundedBankFee?: number;
      refundedInstantPayFee?: number;
      refundedFlashPayFee?: number;
      refundedGSTFee?: number;
      refundedBankPayoutFee?: number;
      refundedMinimumTransactionFee?: number;
      processedAccountID?: string;
      standardMCC?: string;
    }
  ];
  exchangeRate?: number;
  receiptNumber?: string;
  referenceNumber?: string;
  createdAt: string;
  channelFees?: number;
  feeRate?: number;
  origPayoutDate?: string;
  creatorEmail?: string;
  customerEmail?: string;
  customerName?: string;
  updatedAt: string;
  currencyId?: number;
  payoutDate: string;
  payees: PayeeType[];
  /**
   * 1: Rental
   * 2: Salary
   * 3: Invoice
   */
  paymentStatusId: number;
  paymentMethodId?: number;
  scheduledChargeDate: string;
  couponCode?: string;
  company?: string;
  completedAt?: string;
  businessIndustry?: string;
  settlementTime?: string;
  submittedToBankDate?: string;
  isInternationalPayment?: boolean;
  isEnterprisePayment?: boolean;
  isMastercardMovePayment?: boolean;
  isBusiness?: boolean;
  isCryptoPayment?: boolean;
  isDeductedRate?: boolean;
  originalAmount?: number;
  displayCryptoCurrencyId?: number;

  // For payment detail response structure
  supportingDocument: Array<{
    key: string;
    name: string;
    size: number;
    url: string;
  }>;

  amountOff?: number;
  order?: number;
  commissionAmount?: number;
  requesterRates?: CollectionRates[];
  bankTransferFees?: number;
  merchantFees?: number;
  schemeFees?: number;
  interchangeFees?: number;
  settlementFile?: Array<{
    key: string;
    name: string;
    size: number;
    url: string;
  }>;

  refundHistory?: RefundHistoryItem[];

  // For refund pending detail
  refundActionType?: string;
  refundMetadata?: {
    newPaymentAmount: number;
    newPaymentBankPayoutFees: number;
    newPaymentFees: number;
    newPaymentInstantPayFees: number;
    newPaymentFlashPayFees: number;
    newPaymentGstFees: number;
    newPaymentMinimumTransactionFees: number;
    newPaymentTotal: number;
    newRate: number;
    otherRefusalReason: string;
    payees?: {
      refundedAmount: number;
    }[];
    paymentRefNo: string;
    refundedAmount: number;
    refundedBankPayoutFee: number;
    refundedDate: string;
    refundedFee: number;
    refundedBankFee: number;
    refundedInstantPayFee: number;
    refundedFlashPayFee: number;
    refundedGstFee: number;
    refundedMinimumTransactionFee: number;
    refundedTotal: number;
    refusalReason: string;
    refusalReasonCode: string;
  };
  totalFxRateGainLoss?: number;
  dbsRate?: number;
};

export type FeesVerboseType = {
  coupon: string;
  fee: number;
  rate: number;
  rateBeforeCoupon: number;
  savings: number;
  total: number;
};

export type LogChargeAttempt = {
  id: number;
  success: boolean;
  metadata: string;
  errors: string;
  actionType: string;
  last4: string;
  cardBrandId: number;
  name: string;
  cardType: string;
  bankIssuer: string;
  cardFunding: string;
};

export type PayoutPaymentsRequestType = {
  id: number;
  countryId: number;
  createdAt: string;
  currencyCode: string;
  currencyId: number;
  numberOfPayment: number;
  payoutDate: string;
  status: string;
  totalAmount: number;
  payments: [
    {
      id: number;
      accountNumber: string;
      amount: number;
      bank: string;
      bsbCode: string;
      countryId: number;
      currencyId: number;
      email: string;
      payee: string;
      payees: {
        id: number;
        accountNumber: string;
        recipientName: string;
        payeeData: {
          supportingDocuments: Array<string> | null;
        };
        countryId: number;
        createdAt: string;
        email: string;
        accountId: number;
        amount: number;
        bsbCode: string;
        firstName: string;
        lastName: string;
        bankBsbId: number;
        currencyId: number;
        comments: string;
      }[];
      purpose: string;
      receiptNumber: string;
    }
  ];
};

export type PaymentCommentType = {
  id: number;
  createdAt: string;
  updateAt: string | null;
  createdBy: number;
  paymentId: number;
  comment: string;
  commentedBy: string;
};

export type EnterprisePaymentsType = {
  buyerFee: number;
  cardLast4Digits: string;
  chargeDate: string;
  countryCode: string;
  currencyCode: string;
  gst: number;
  invoiceAmount: number;
  method: string;
  noteToSupplier: string;
  payoutDate: string;
  receiptNumber: string;
  requesterEmail: string;
  requesterName: string;
  supplierName: string;
  supplierFee: number;
  totalAmount: number;
};

export type exchangeRateDetailType = {
  old_rate?: number;
  new_rate?: number;
  old_amount?: number;
  new_amount?: number;
};

export type ReinitiateWallexPaymentRequest = RefundCommonAttrs & {
  metaData: [
    {
      newRateWallexPayment?: number;
      newAmountWallexPayment?: number;
      expiresAt?: string;
    }
  ];
  oldMetaData: [
    {
      currentRateWallexPayment?: number;
      currentAmountWallexPayment?: number;
    }
  ];
};

export type BulkRequestType = {
  id: number;
  actionType: string;
  createdAt: string;
  createdBy: {
    email: string;
    firstName: string;
    lastName: string;
    userId: number;
  };
  metaData: {
    amount?: number;
    bankName?: string;
    countryId?: number;
    swiftCode?: string;
    isInternalBank?: boolean;
  };
  oldMetaData: {
    amount?: number;
  };
} & ProcessedByType;

export type ProcessedByType = {
  processedBy?: {
    email: string;
    firstName: string;
    lastName: string;
    userId: number;
  };
};

export type DbsUapiReviewBatch = {
  currencyId: number;
  batchId: number;
  txnCount: number;
  totalAmount: number; // minor units (cents)
};

export type DbsUapiExcludedPayment = {
  paymentId: number;
  receiptNumber: string;
  reasons: string[];
};

export type DbsUapiSummary = {
  action: string;
  paymentIds: number[];
  txnCount: number; // payee lines, not payments
  reviewedBy: string[];
  excluded: DbsUapiExcludedPayment[];
  message: string;
};

export type DbsUapiReviewResult = {
  kind: "review" | "approve";
  batches: DbsUapiReviewBatch[];
  reviewed: number;
  paymentIds: number[];
  excluded: DbsUapiExcludedPayment[];
};

export type RefundRequestType = (
  | RefundType
  | EditPaymentPayeeRequest
  | ReinitiateWallexPaymentRequest
) &
  ProcessedByType;

export type PaymentState = {
  readonly isFetching: boolean;
  readonly selectedPaymentId: number;
  readonly selectedPaymentIds: number[];
  readonly payments: PaymentType[];
  readonly schedules: ScheduleType[];
  readonly scheduleRequests: ScheduleRequestType[];
  readonly selectedScheduleId: number;
  readonly refundRequests: RefundRequestType[];
  readonly refundRequesteds: RefundRequestType[];
  readonly total: number;
  readonly sumPaymentAmount: number;
  readonly sumPaymentTotal: number;
  readonly totalStripeTransactions: number;
  readonly totalBankPayout: number;
  readonly totalBatch: number;
  readonly activity: {
    scheduled?: number;
    inprogress?: number; // include onhold payment
    completed?: number;
  };
  readonly sumPaymentInThisMonth: {
    scheduled?: number;
    inprogress?: number; // include onhold payment
    completed?: number;
  };
  readonly feesVerbose: { [paymentId: number]: FeesVerboseType };
  readonly updatedPaymentStatus: {
    success: boolean;
    failed: boolean;
  };
  readonly logChargeAttempt: LogChargeAttempt[];
  readonly payoutPaymentsRequests: PayoutPaymentsRequestType[];
  readonly selectedPaymentComments: PaymentCommentType[];
  readonly enterprisePayments: EnterprisePaymentsType[];
  readonly instantPayLimit: number;
  readonly exchangeRateDetail: exchangeRateDetailType;
  readonly selectedSettlementCurrency: string;
  readonly bulkRequests: BulkRequestType[];
  readonly refundRequestsHistoryAppproved: RefundRequestType[];
  readonly scheduleRequestsHistoryApproved: ScheduleRequestType[];
  readonly bulkRequestHistoryApproved: BulkRequestType[];
  readonly isFetchingRequestApproved: boolean;
  readonly totalRequests: number;
  readonly dbsUapiReviewResult: DbsUapiReviewResult | null;
};

const defaultState: PaymentState = {
  activity: {},
  feesVerbose: {},
  isFetching: false,
  payments: [],
  refundRequests: [],
  refundRequesteds: [],
  schedules: [],
  scheduleRequests: [],
  selectedPaymentId: -1,
  selectedPaymentIds: [],
  selectedScheduleId: -1,
  sumPaymentAmount: 0,
  sumPaymentInThisMonth: {},
  sumPaymentTotal: 0,
  total: 0,
  totalBankPayout: 0,
  totalBatch: 0,
  totalStripeTransactions: 0,
  updatedPaymentStatus: { success: false, failed: false },
  logChargeAttempt: [],
  payoutPaymentsRequests: [],
  selectedPaymentComments: [],
  enterprisePayments: [],
  instantPayLimit: 0,
  exchangeRateDetail: {},
  selectedSettlementCurrency: "",
  bulkRequests: [],
  refundRequestsHistoryAppproved: [],
  scheduleRequestsHistoryApproved: [],
  bulkRequestHistoryApproved: [],
  isFetchingRequestApproved: false,
  totalRequests: 0,
  dbsUapiReviewResult: null
};

export default (state: PaymentState, action: types.Action) => {
  if (_isEmpty(state)) {
    state = defaultState;
  }

  switch (action.type) {
    case getType(actions.setPayments):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        payments: action.payload.payments
          ? action.payload.payments
          : state.payments,
        sumPaymentAmount: action.payload.sumPaymentAmount,
        sumPaymentTotal: action.payload.sumPaymentTotal,
        total: action.payload.total,
        totalBankPayout: action.payload.totalBankPayout,
        totalBatch: action.payload.totalBatch,
        totalStripeTransactions: action.payload.totalStripeTransactions
      };
    case getType(actions.appendPayments):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        payments: state.payments.concat(action.payload.payments)
      };
    case getType(actions.setRefundRequests):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        refundRequests: action.payload.refundRequests,
        totalRequests: action.payload.totalRequest
      };
    case getType(actions.setRefundRequesteds):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        refundRequesteds: action.payload.refundRequesteds
      };
    case getType(actions.setSchedules):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        schedules: action.payload.schedules,
        total: action.payload.total
      };
    case getType(actions.updatePaymentDetail):
      const idx = _findIndex(state.payments, o => o.id === action.payload.id);
      const payments = state.payments;
      if (idx > -1 && action.payload.payment) {
        payments.splice(idx, 1, action.payload.payment);
      } else if (action.payload.payment) {
        payments.push(action.payload.payment);
      }

      return {
        ...state,
        payments,
        selectedPaymentId: action.payload.id
      };
    case getType(actions.setPaymentActivity):
      return {
        ...state,
        activity: action.payload.activity
      };

    case getType(actions.setSumPaymentInThisMonth):
      return {
        ...state,
        sumPaymentInThisMonth: action.payload.sumPaymentInThisMonth
      };

    case getType(actions.setScheduleDetail):
      const schedules = state.schedules;

      const sIdx = _findIndex(
        schedules,
        o => o.scheduleId === action.payload.scheduleId
      );
      const scheduleDetail = schedules
        .filter(s => s.scheduleId === action.payload.scheduleId)
        .map(ns => ({
          ...ns,
          lastPayout: action.payload.lastPayout,
          paymentSetting: action.payload.paymentSetting,
          upcomingPayment: {
            payments: action.payload.payments
          },
          accountId: action.payload.accountId,
          ...(action.payload.upComingPayoutDate && {
            upComingPayoutDate: action.payload.upComingPayoutDate
          })
        }))[0];

      if (sIdx > -1) {
        schedules.splice(sIdx, 1, scheduleDetail);
      }

      return {
        ...state,
        schedules
      };
    case getType(actions.selectSchedule):
    case getType(actions.adminSelectSchedule):
      return {
        ...state,
        selectedScheduleId: action.payload.id
      };

    case getType(actions.setScheduleRequests):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        scheduleRequests: action.payload.scheduleRequests,
        totalRequests: action.payload.totalRequests
      };
    case getType(actions.selectPayments):
      return {
        ...state,
        selectedPaymentIds: action.payload.ids
      };

    case getType(actions.setFeesVerbose):
      return {
        ...state,
        feesVerbose: action.payload.feesVerbose
      };

    case getType(actions.setUpdatedPaymentStatus):
      return {
        ...state,
        updatedPaymentStatus: action.payload.updatedPaymentStatus
      };

    case getType(actions.setLogChargeAttempt):
      return {
        ...state,
        logChargeAttempt: action.payload.logChargeAttempt
      };
    case getType(actions.setPayoutPaymentsRequests):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        payoutPaymentsRequests: action.payload.payoutPaymentsRequests
      };
    case getType(actions.setSelectedPaymentComments):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        selectedPaymentComments: action.payload.selectedPaymentComments
      };

    case getType(actions.setEnterprisePayments):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        enterprisePayments: action.payload.enterprisePayments,
        sumPaymentAmount: action.payload.sumPaymentAmount,
        sumPaymentTotal: action.payload.sumPaymentTotal,
        total: action.payload.total,
        totalBankPayout: action.payload.totalBankPayout,
        totalStripeTransactions: action.payload.totalStripeTransactions
      };

    case getType(actions.setInstantPayLimit):
      return {
        ...state,
        isFetching: action.payload.isFetching,
        instantPayLimit: action.payload.instantPayLimit
      };
    case getType(actions.setExchangeRateDetail):
      return {
        ...state,
        exchangeRateDetail: action.payload.exchangeRateDetail
      };

    case getType(actions.selectSettlementCurrency):
      return {
        ...state,
        selectedSettlementCurrency: action.payload.selectedSettlementCurrency
      };
    case getType(actions.setBulkRequests):
      return {
        ...state,
        bulkRequests: action.payload.bulkRequests,
        totalRequests: action.payload.totalRequests
      };

    case getType(actions.setRefundRequestsHistoryAppproved):
      return {
        ...state,
        isFetchingRequestApproved: action.payload.isFetching,
        refundRequestsHistoryAppproved:
          action.payload.refundRequestsHistoryAppproved
      };

    case getType(actions.setScheduleRequesHistoryApproved):
      return {
        ...state,
        isFetchingRequestApproved: action.payload.isFetching,
        scheduleRequestsHistoryApproved:
          action.payload.scheduleRequestsHistoryApproved
      };

    case getType(actions.setBulkRequesHistoryApproved):
      return {
        ...state,
        isFetchingRequestApproved: action.payload.isFetching,
        bulkRequestHistoryApproved: action.payload.bulkRequestHistoryApproved
      };

    case getType(actions.setDbsUapiReviewResult):
      return {
        ...state,
        dbsUapiReviewResult: action.payload.result
      };
  }
  return state;
};
